// gti-chms-frontend/src/pages/FinancesPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import ConfirmationModal from '../../components/common/ConfirmationModal';

import FinancialRecordFormModal from '../../components/finances/FinancialRecordFormModal';
import type { Account, FinancialRecord } from '../../models';


const FinancesPage: React.FC = () => {
    const { user, authLoading } = useAuth();
    const [financialRecords, setFinancialRecords] = useState<FinancialRecord[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]); // State for accounts
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Modal states
    const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);
    const [recordToEdit, setRecordToEdit] = useState<FinancialRecord | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [recordToDelete, setRecordToDelete] = useState<FinancialRecord | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const recordsResponse = await api.get<FinancialRecord[]>('/finances');
            setFinancialRecords(recordsResponse.data);

            // Fetch accounts (only if user is admin/super-admin)
            if (user && (user.role === 'admin' || user.role === 'super-admin')) {
                const accountsResponse = await api.get<Account[]>('/accounts');
                setAccounts(accountsResponse.data);
            }
        } catch (err: any) {
            console.error('Failed to fetch financial data:', err);
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching financial data.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (!authLoading && user && (user.role === 'admin' || user.role === 'super-admin')) {
            fetchData();
        } else if (!authLoading && (!user || (user.role !== 'admin' && user.role !== 'super-admin'))) {
            setError('You do not have permission to view this page.');
            setLoading(false);
        }
    }, [user, authLoading, fetchData]);

    // Handlers for Add/Edit/Delete Financial Record
    const handleSaveRecord = async (recordData: any, recordId?: string) => {
        try {
            if (recordId) {
                await api.put(`/finances/${recordId}`, recordData);
            } else {
                await api.post('/finances', recordData);
            }
            await fetchData(); // Refresh the list
            setIsFormModalOpen(false);
            setRecordToEdit(null);
        } catch (err: any) {
            console.error("Error saving financial record:", err);
            throw err; // Re-throw to be caught by modal's internal error state
        }
    };

    const handleDeleteConfirm = async () => {
        if (!recordToDelete) return;
        try {
            await api.delete(`/finances/${recordToDelete._id}`);
            await fetchData(); // Refresh the list
            setIsDeleteModalOpen(false);
            setRecordToDelete(null);
        } catch (err: any) {
            console.error("Error deleting financial record:", err);
            setError(err.response?.data?.message || err.message || 'Failed to delete financial record.');
        }
    };

    // Modal Open/Close Handlers
    const openAddModal = () => {
        setRecordToEdit(null);
        setIsFormModalOpen(true);
    };
    const openEditModal = (record: FinancialRecord) => {
        setRecordToEdit(record);
        setIsFormModalOpen(true);
    };
    const closeFormModal = () => {
        setIsFormModalOpen(false);
        setRecordToEdit(null);
    };
    const openDeleteModal = (record: FinancialRecord) => {
        setRecordToDelete(record);
        setIsDeleteModalOpen(true);
    };
    const closeDeleteModal = () => {
        setRecordToDelete(null);
        setIsDeleteModalOpen(false);
    };


    if (loading || authLoading) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                <p className="ml-4 text-gray-700">Loading financial data...</p>
            </div>
        );
    }

    if (error && (!user || (user.role !== 'admin' && user.role !== 'super-admin'))) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Access Denied!</strong>
                <span className="block sm:inline ml-2">{error}</span>
            </div>
        );
    }

    // Display access denied if user is not admin/super-admin but the component is rendered
    if (!user || (user.role !== 'admin' && user.role !== 'super-admin')) {
        return (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Permission Required!</strong>
                <span className="block sm:inline ml-2">You must be an Admin or Super Admin to view financial records.</span>
            </div>
        );
    }


    return (
        <div className="bg-white p-6 rounded-lg shadow-md min-h-[calc(100vh-120px)] flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-semibold text-gray-800">Financial Records</h2>
                <button
                    onClick={openAddModal}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-150 ease-in-out"
                >
                    Add New Record
                </button>
            </div>

            {error && financialRecords.length > 0 && ( // Show error as banner if some records loaded
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <strong className="font-bold">Error:</strong>
                    <span className="block sm:inline ml-2">{error}</span>
                </div>
            )}

            {financialRecords.length === 0 ? (
                <p className="text-gray-600 text-lg text-center mt-10">No financial records found. Start by adding one!</p>
            ) : (
                <div className="overflow-x-auto flex-grow">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Category
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Account
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Description
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Recorded By
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {financialRecords.map((record:any) => (

                                <tr key={record._id}>
                                


                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(record.recordDate).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            record.recordType === 'Income' || record.recordType === 'Donation' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                            {record.recordType}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {record.category}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                        {record.currency} {record.amount.toFixed(2)}
                                    </td>
                                    {/* NEW: Display Account Name */}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {record.accountId ? `${record.accountId.name} (${record.accountId.accountIdentifier})` : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={record.description}>
                                        {record.description || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {record.recordedBy?.firstName} {record.recordedBy?.lastName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                        <button
                                            onClick={() => openEditModal(record)}
                                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                                            title="Edit Record"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => openDeleteModal(record)}
                                            className="text-red-600 hover:text-red-900"
                                            title="Delete Record"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Financial Record Add/Edit Modal */}
            <FinancialRecordFormModal
                isOpen={isFormModalOpen}
                onClose={closeFormModal}
                onSave={handleSaveRecord}
                recordToEdit={recordToEdit}
                accounts={accounts} // Pass accounts to the form
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleDeleteConfirm}
                message={`Are you sure you want to delete this ${recordToDelete?.recordType} record for ${recordToDelete?.currency} ${recordToDelete?.amount.toFixed(2)}? This will also adjust the account balance.`}
                confirmText="Delete"
                isDestructive={true}
            />
        </div>
    );
};

export default FinancesPage;
